import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Music4 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, ASSET_BASE } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { useLocale } from "@/lib/LocaleContext";
import { localisedTo } from "@/lib/i18nRoutes";
import { formatTrackTitle, trackDownloadName } from "@/lib/trackTitle";
import { downloadAuthenticatedBlob, fetchTrackDownloadBlob } from "@/lib/download";
import Nav from "@/components/Nav";
import VerifyEmailBanner from "@/components/VerifyEmailBanner";

export default function Library() {
  const { t } = useTranslation();
  const { lang } = useLocale();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/my/purchases").then((r) => setItems(r.data));
  }, [user]);

  const onDownload = async (item) => {
    // Double-tap guard — silently ignore a repeat click while a
    // download is being prepared.
    if (downloadingId) return;
    setDownloadingId(item.track_id);
    try {
      await downloadAuthenticatedBlob({
        trackId: item.track_id,
        fetchBlob: () => fetchTrackDownloadBlob(item.track_id),
        filename: `${trackDownloadName(item)}.mp3`,
        onError: async (err) => {
          const status = err?.response?.status;
          if (status === 403) {
            let detail = err?.response?.data;
            try {
              if (detail instanceof Blob) detail = JSON.parse(await detail.text());
            } catch (_) {}
            if (detail?.detail?.code === "email_not_verified") {
              toast.error(t("verifyEmail.actionDownload"));
              return true;
            }
          }
          toast.error(t("library.downloadFailed"));
          return true;
        },
      });
    } finally {
      setDownloadingId(null);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-6">
        <div className="hairline p-10 rounded-2xl text-center">
          <div className="font-anton text-3xl">{t("auth.signInRequired")}</div>
          <p className="text-white/60 mt-2">{t("auth.signInRequiredBody")}</p>
          <Link to={localisedTo("/login", lang)} data-testid="library-signin" className="btn-primary mt-6 inline-flex">{t("nav.signIn")}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <Nav />
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 pt-32 pb-24">
        <p className="font-mono text-xs text-red-brand tracking-[0.3em]">{t("library.kicker")}</p>
        <h1 className="font-anton text-6xl md:text-7xl mt-3">{t("library.title")}</h1>
        <p className="text-white/60 mt-3">{t("library.lead")}</p>

        <div className="mt-8"><VerifyEmailBanner /></div>

        {items.length === 0 ? (
          <div className="mt-12 hairline rounded-2xl p-14 text-center text-white/50" data-testid="library-empty">
            <Music4 size={40} className="mx-auto text-white/20" />
            <div className="mt-4">{t("library.empty")}</div>
            <Link to={localisedTo("/#music", lang)} data-testid="library-browse" className="btn-primary mt-6 inline-flex">{t("library.browse")}</Link>
          </div>
        ) : (
          <div className="mt-10 space-y-3" data-testid="library-list">
            {items.map((it) => (
              <div key={it.purchase_id} className="hairline rounded-xl p-4 bg-white/[0.02] flex items-center gap-4">
                <div className="w-16 h-16 rounded-lg bg-[#111] flex items-center justify-center overflow-hidden">
                  {it.cover_url ? <img src={`${ASSET_BASE}${it.cover_url}`} alt="" className="w-full h-full object-cover" /> : <Music4 size={22} className="text-white/40" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg truncate">{formatTrackTitle(it)}</div>
                  <div className="text-white/50 text-xs font-mono" dir="ltr">AED {Number(it.amount || 0).toFixed(2)} · {new Date(it.paid_at).toLocaleDateString()}</div>
                  {it.pack_name && (
                    <div className="mt-1 inline-block font-mono text-[9px] tracking-[0.25em] text-red-brand border border-red-brand/40 rounded px-1.5 py-0.5" data-testid={`library-pack-badge-${it.track_id}`}>
                      {t("packs.cartPackLabel")} · {it.pack_name}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => onDownload(it)}
                  disabled={downloadingId === it.track_id}
                  data-testid={`library-download-${it.track_id}`}
                  className="btn-ghost !py-2 !px-4 text-sm disabled:opacity-60 disabled:cursor-wait"
                >
                  <Download size={14} /> {downloadingId === it.track_id ? t("library.preparing") : t("library.download")}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
